import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useTheme } from '@material-ui/core/styles';

import { makeRowColumn, getHeadObj } from 'lib/commonLib';
import { FluctuationScriptStatisticsData } from 'lib/api/media';

import MbCheckbox from 'components/atoms/MbCheckbox';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import NewTableContainer from 'components/molecules/NewTable';
import FluctuationScriptGraph from './FluctuationScriptGraph';

const FluctuationScriptStatistics = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [initialization, setInitialization] = useState(false);

  let initParams = {
    sort: 'desc',
    order: 'ADVRTS_AMT',
    pltfomTpCode: 'all',
    advrtsAmt: false,
    displayType: 'mediaScriptNo',
    sDate: moment().add(-2, 'day').format('YYYYMMDD'),
    eDate: moment().add(-2, 'day').format('YYYYMMDD'),
    diffSDate: moment().add(-1, 'day').format('YYYYMMDD'),
    diffEDate: moment().add(-1, 'day').format('YYYYMMDD'),
  };
  const [params, setParams] = useState(initParams);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  // header 및 body 컬럼 커스텀
  const customized = {
    ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'ADVRTS_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const advrtsAmt = getHeadObj(headsMap, 'ADVRTS_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['ADVRTS_AMT'],
              advrtsAmt.type,
              advrtsAmt.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderLeft: theme.palette.table.border.screen,
      },
      bodyStyle: { borderLeft: theme.palette.table.border.screen },
    },
    DIFF_ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'ADVRTS_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const advrtsAmt = getHeadObj(headsMap, 'DIFF_ADVRTS_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_ADVRTS_AMT'],
              advrtsAmt.type,
              advrtsAmt.format
            )}
          </>
        );
      },
      headStyle: { backgroundColor: '#d4f5d8' },
    },
    RAF_ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {t('비교(비교일)')}
            <br />({getHeadObj(headsMap, 'ADVRTS_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_ADVRTS_AMT');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_ADVRTS_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['RAF_ADVRTS_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_ADVRTS_AMT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderRight: theme.palette.table.border.screen,
      },
      bodyStyle: { borderRight: theme.palette.table.border.screen },
    },
    RAF_RATE_ADVRTS_AMT: {
      merged: true,
    },
    PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'PAR_EPRS_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const parEprsCnt = getHeadObj(headsMap, 'PAR_EPRS_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['PAR_EPRS_CNT'],
              parEprsCnt.type,
              parEprsCnt.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    DIFF_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'PAR_EPRS_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DIFF_PAR_EPRS_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    RAF_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'PAR_EPRS_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_PAR_EPRS_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['RAF_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_PAR_EPRS_CNT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    RAF_RATE_PAR_EPRS_CNT: {
      merged: true,
    },
    REQ_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'REQ_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'REQ_CNT');
        return (
          <>
            {makeRowColumn(rowObj['REQ_CNT'], bodyItem1.type, bodyItem1.format)}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderLeft: theme.palette.table.border.screen,
      },
      bodyStyle: { borderLeft: theme.palette.table.border.screen },
    },
    DIFF_REQ_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'REQ_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DIFF_REQ_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_REQ_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
      },
    },
    RAF_REQ_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'REQ_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_REQ_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_REQ_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['RAF_REQ_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_REQ_CNT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderRight: theme.palette.table.border.screen,
      },
      bodyStyle: { borderRight: theme.palette.table.border.screen },
    },
    RAF_RATE_REQ_CNT: {
      merged: true,
    },
    CLICK_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'CLICK_RATE').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['CLICK_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
        borderLeft: theme.palette.table.border.screen,
      },
      bodyStyle: { borderLeft: theme.palette.table.border.screen },
    },
    DIFF_CLICK_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'CLICK_RATE').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DIFF_CLICK_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_CLICK_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    RAF_CLICK_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'CLICK_RATE').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_CLICK_RATE');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_CLICK_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['RAF_CLICK_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_CLICK_RATE'],
              bodyItem2.type,
              bodyItem2.format
            )}
            % )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    RAF_RATE_CLICK_RATE: {
      merged: true,
    },
    SESSION_ORDER_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'SESSION_ORDER_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'SESSION_ORDER_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['SESSION_ORDER_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderLeft: theme.palette.table.border.screen,
      },
      bodyStyle: { borderLeft: theme.palette.table.border.screen },
    },
    DIFF_SESSION_ORDER_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'SESSION_ORDER_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DIFF_SESSION_ORDER_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_SESSION_ORDER_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </>
        );
      },
      headStyle: { backgroundColor: '#d4f5d8' },
    },
    RAF_SESSION_ORDER_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'SESSION_ORDER_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_SESSION_ORDER_AMT');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_SESSION_ORDER_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['RAF_SESSION_ORDER_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_SESSION_ORDER_AMT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            % )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d4f5d8',
        borderRight: theme.palette.table.border.screen,
      },
      bodyStyle: { borderRight: theme.palette.table.border.screen },
    },
    RAF_RATE_SESSION_ORDER_AMT: {
      merged: true,
    },
    SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.sDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.eDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'SESSION_ROAS').label})(
            {t('모비온')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'SESSION_ROAS');
        const bodyItem2 = getHeadObj(headsMap, 'MOB_SESSION_ROAS');
        return (
          <>
            {makeRowColumn(
              rowObj['SESSION_ROAS'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['MOB_SESSION_ROAS'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
        borderLeft: theme.palette.table.border.screen,
      },
      bodyStyle: { borderLeft: theme.palette.table.border.screen },
    },
    MOB_SESSION_ROAS: {
      merged: true,
    },
    DIFF_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <>
            {moment(params.diffSDate, 'YYYYMMDD').format('MM-DD')} ~
            {moment(params.diffEDate, 'YYYYMMDD').format('MM-DD')} {t('평균')}
            <br />({getHeadObj(headsMap, 'SESSION_ROAS').label}) ({t('모비온')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DIFF_SESSION_ROAS');
        const bodyItem2 = getHeadObj(headsMap, 'DIFF_MOB_SESSION_ROAS');
        return (
          <>
            {makeRowColumn(
              rowObj['DIFF_SESSION_ROAS'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['DIFF_MOB_SESSION_ROAS'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    DIFF_MOB_SESSION_ROAS: {
      merged: true,
    },
    RAF_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <>
            {t('비교(비교일)')}
            <br />({getHeadObj(headsMap, 'SESSION_ROAS').label}) ({t('모비온')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RAF_SESSION_ROAS');
        const bodyItem2 = getHeadObj(headsMap, 'RAF_RATE_SESSION_ROAS');
        const bodyItem3 = getHeadObj(headsMap, 'RAF_RATE_MOB_SESSION_ROAS');

        return (
          <>
            {makeRowColumn(
              rowObj['RAF_SESSION_ROAS'],
              bodyItem1.type,
              bodyItem1.format
            )}
            (
            {makeRowColumn(
              rowObj['RAF_RATE_SESSION_ROAS'],
              bodyItem2.type,
              bodyItem2.format
            )}
            ) (
            {makeRowColumn(
              rowObj['RAF_RATE_MOB_SESSION_ROAS'],
              bodyItem3.type,
              bodyItem3.format
            )}
            )
          </>
        );
      },
      headStyle: {
        backgroundColor: '#d7efff',
      },
    },
    RAF_RATE_SESSION_ROAS: {
      merged: true,
    },

    RAF_RATE_MOB_SESSION_ROAS: {
      merged: true,
    },
    // RAF_MOB_SESSION_ROAS: {
    //   merged: true,
    // },
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const handleChangeValue = (e) => {
    const { name } = e.target;
    const newParams = {
      ...params,
      [name]: !params[name],
    };
    getParam(newParams);
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useDiffDate
          useExternalFilter={false}
        />
        <FormControlLabel
          control={
            <MbCheckbox
              name="advrtsAmt"
              onChange={handleChangeValue}
              checked={params.advrtsAmt}
            />
          }
          label="5만원이상매체"
        />
      </NewFilterWrap>
      <FluctuationScriptGraph
        params={{
          ...params,
          advrtsAmt: params.advrtsAmt ? 'Y' : 'N',
        }}
      />
      <NewTableContainer
        dataReqPromise={FluctuationScriptStatisticsData}
        params={{
          ...params,
          advrtsAmt: params.advrtsAmt ? 'Y' : 'N',
        }}
        getParam={getParam}
        customized={customized}
        useFetch={false}
        useSearch={false}
        rowsPerPage={50}
      />
    </>
  );
};

export default FluctuationScriptStatistics;
